/**
 * Calculate estimated time of arrival (ETA) in minutes based on city and distance
 * @param cityId - The city identifier (DAD, SGN, HAN, etc.)
 * @param distance - Distance in meters
 * @returns ETA in minutes or null if cannot calculate
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
export function calculateETA(
  cityId: string | null,
  distance: number
): number | null {
  if (!cityId || !distance) return null;

  const km = distance / 1000;
  let mins = 0;

  switch (cityId) {
    case 'DAD':
      if (km < 2) mins = 15;
      else if (km < 3) mins = 20;
      else mins = 25;
      break;
    case 'SGN':
      if (km < 2) mins = 20;
      else if (km < 3) mins = 25;
      else mins = 35;
      break;
    case 'HAN':
      if (km < 2) mins = 25;
      else if (km < 3) mins = 30;
      else mins = 35;
      break;
    case 'VCL':
    case 'DNI':
    case 'HDG':
      if (km < 2) mins = 10;
      else if (km < 3) mins = 15;
      else mins = 20;
      break;
    default:
      return null;
  }

  return mins;
}

/**
 * Nửa chu vi Trái Đất (m) — khoảng cách bề mặt lớn nhất về mặt hình học.
 * Bất cứ giá trị nào ≥ ngưỡng này chắc chắn là sentinel, không phải số đo thật.
 */
const MAX_REAL_DISTANCE_M = 20_038_000;

/**
 * Khoảng cách BE trả về có phải số đo thật không.
 *
 * Khi request KHÔNG kèm lat/lng, BE trả `Double.MAX_VALUE`
 * (`1.7976931348623157e+308`) làm sentinel "không biết". Không lọc thì:
 *   - `formatDistance` in ra "1.7976931348623156e+305 km" ngay trên header quán;
 *   - mọi so sánh kiểu `distance > 20000` đều đúng, nên dialog "ngoài vùng giao
 *     hàng" tự bung dù user còn chưa nhập địa chỉ nào.
 *
 * MỌI chỗ đọc `store.distance` / `item.distance` phải đi qua hàm này trước.
 */
export function isUsableDistance(distance?: number | null): distance is number {
  return (
    typeof distance === 'number' &&
    Number.isFinite(distance) &&
    distance > 0 &&
    distance < MAX_REAL_DISTANCE_M
  );
}

/**
 * Format distance from meters to kilometers with 1 decimal place
 * @param distance - Distance in meters
 * @returns Formatted distance string (e.g., "2.5 km")
 */
export function formatDistance(distance: number): string {
  return `${(distance / 1000).toFixed(1)} km`;
}

/**
 * Format distance with meter/kilometer logic like SoloMealItemCard
 * @param distance - Distance in meters
 * @returns Formatted distance string (e.g., "500m" or "2.5km")
 */
export function formatDistanceMeterKilometer(distance: number): string {
  return distance < 1000
    ? `${Math.round(distance)}m`
    : `${(distance / 1000).toFixed(1)}km`;
}

/**
 * Get ETA display text with distance and time
 * @param cityId - The city identifier
 * @param distance - Distance in meters
 * @returns Formatted ETA text or null if cannot calculate
 */
export function getETADisplayText(
  cityId: string | null,
  distance: number
): string | null {
  const etaMinutes = calculateETA(cityId, distance);
  if (etaMinutes === null) return null;

  const distanceText = formatDistance(distance);
  return `${distanceText} • ${etaMinutes} phút trở lên`;
}
